import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import api from '../../../services/api-configuration-v3';

/**
 * Teslim edilemeyen şase bildirimi için modal komponenti
 * @param {Object} props - Komponent props'ları
 * @returns {JSX.Element} Teslim edilemeyen şase modal
 */
const UndeliveredChassisModal = ({
    isOpen,      // Modal'ın açık/kapalı durumu
    toggle,      // Modal'ı açıp/kapatan fonksiyon
    refreshData  // Ana sayfadaki verileri yenileyen fonksiyon
}) => {
    // İşlem durumu state'i
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isError, setIsError] = useState(false);

    /**
     * Şase numarası değişikliğini handle eden fonksiyon
     */
    const handleSaseChange = () => {
        const saseInput = document.getElementById('sase-input');
        if (saseInput) {
            saseInput.value = saseInput.value.toUpperCase().trim();
        }
    };

    /**
     * Açıklama değişikliğini handle eden fonksiyon
     */
    const handleAciklamaChange = () => {
        const aciklamaInput = document.getElementById('aciklama-input');
        if (aciklamaInput) {
            console.log(aciklamaInput.value);
        }
    };

    /**
     * Form validasyonunu yapan fonksiyon
     * @returns {boolean|Object} Validasyon sonucu
     */
    const validateForm = () => {
        const saseInput = document.getElementById('sase-input');
        const aciklamaInput = document.getElementById('aciklama-input');

        const trimmedSase = saseInput?.value?.trim() || '';
        const trimmedAciklama = aciklamaInput?.value?.trim() || '';

        if (!trimmedSase) {
            toast.error('Lütfen şase numarasını girin');
            return false;
        }

        if (trimmedSase.length !== 17) {
            toast.error('Şase numarası 17 haneli olmalıdır');
            return false;
        }

        if (!trimmedAciklama) {
            toast.error('Lütfen açıklama girin');
            return false;
        }

        if (trimmedAciklama.length < 5) {
            toast.error('Açıklama en az 5 karakter olmalıdır');
            return false;
        }

        return { trimmedSase, trimmedAciklama };
    };

    /**
     * Form gönderimini handle eden fonksiyon
     */
    const handleSubmit = async () => {
        if (isSubmitting) return;

        const validationResult = validateForm();
        if (!validationResult) return;

        const { trimmedSase, trimmedAciklama } = validationResult;

        try {
            setIsSubmitting(true);

            // Teslim edilemeyen şase bildirimi
            const response = await api.post('/deleteDeliveryDateByChassis', {
                chassis_no: trimmedSase,
                reason: trimmedAciklama,
                user_id: parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii')),
                employee_id: parseInt(Buffer.from(localStorage.getItem("employee_id"), 'base64').toString('ascii'))
            });

            // Check if response indicates failure
            if (response.data && response.data.success === false) {
                toast.error(response.data.error || 'İşlem başarısız');
                return;
            }

            // Ana sayfadaki verileri yenile
            refreshData();

            toast.success('Bildirim başarıyla gönderildi');

            // Form alanlarını temizle
            const saseInput = document.getElementById('sase-input');
            const aciklamaInput = document.getElementById('aciklama-input');
            if (saseInput) saseInput.value = '';
            if (aciklamaInput) aciklamaInput.value = '';

            toggle();

        } catch (error) {
            let errorMessage = error.message?.replace("failed to mark chassis as undeliverable: ", "Teslim Edilemeyen Şase: ") || 'Bir hata oluştu';

            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle} className="border-0 pb-0">
                <h4 className="mb-0 fw-semibold">Teslim Edilemeyen Şase Bildir</h4>
            </ModalHeader>

            <ModalBody className="px-4">
                <div className="alert alert-dark d-flex align-items-center" role="alert">
                    <i className="fa fa-info-circle me-2"></i>
                    <b>İlk önce grupajın diğer araçları için teslim tarihi girilmeli, sonra şase bazında bildirim yapılmalıdır</b>
                </div>

                <div className="mb-4">
                    <Label className="form-label required fw-medium">
                        Şase
                        <small className="text-muted ms-2">(17 haneli)</small>
                    </Label>
                    <Input
                        id="sase-input"
                        type="text"
                        className="form-control"
                        placeholder="17 haneli şase numarasını girin"
                        onChange={handleSaseChange}
                        maxLength={17}
                        disabled={isSubmitting}
                    />
                </div>

                <div className="mb-4">
                    <Label className="form-label required fw-medium">
                        Açıklama
                        <small className="text-muted ms-2">(En az 5 karakter)</small>
                    </Label>
                    <Input
                        id="aciklama-input"
                        type="textarea"
                        className={`form-control ${isError ? 'is-invalid' : ''}`}
                        placeholder="Açıklama girin (en az 5 karakter)"
                        rows={4}
                        onChange={handleAciklamaChange}
                        disabled={isSubmitting}
                    />
                </div>
            </ModalBody>

            <ModalFooter className="border-0 px-4">
                <button
                    className="btn btn-light px-4"
                    onClick={toggle}
                    disabled={isSubmitting}
                >
                    İptal
                </button>
                <button
                    className="btn btn-dark px-4"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting && <i className="fa fa-spinner fa-spin me-2"></i>}
                    Onayla
                </button>
            </ModalFooter>

            <style jsx>{`
                :global(.form-label.required::after) {
                    content: "*";
                    color: #dc3545;
                    margin-left: 4px;
                }
            `}</style>
        </Modal>
    );
};

// Prop tipleri tanımlaması
UndeliveredChassisModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    refreshData: PropTypes.func.isRequired
};

export default UndeliveredChassisModal; 